import type { FormEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { getProfileMe, type ProfileMeResponse,updateProfileMe } from '../api/profile'
import { ChromePrimaryButton } from '../components/ChromeActions'
import { chromeInputClass } from '../components/ChromeSurface'
import { LogoutButton } from '../components/LogoutButton'
import { ThemeReveal, ThemeStagger, ThemeStaggerItem } from '../components/motion/ThemeMotion'
import { ProfilePlanStrip } from '../components/profile/ProfilePlanStrip'
import { ProfileStorageSettingsSection } from '../components/profile/ProfileStorageSettingsSection'
import { UserPermGate } from '../components/UserPermGate'
import { useToast } from '../context/ToastContext'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { ContentSkeleton } from '../layouts/ContentSkeleton'
import { useTheme } from '../theme/ThemeContext'

const PAGE_SIZE = 8

export function ProfilePage() {
  const { theme } = useTheme()
  const { showToast } = useToast()
  const angular = theme === 'pixel' || theme === 'geek'
  const [profile, setProfile] = useState<ProfileMeResponse | null>(null)
  const [nickname, setNickname] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [creditPage, setCreditPage] = useState(1)
  const canSaveCloudStorage = useUserPerm(USER_PERMISSION.storageGalleryOwn)

  const loadProfile = async (page = creditPage) => {
    setLoading(true)
    try {
      const data = await getProfileMe(page, PAGE_SIZE)
      setProfile(data)
      setNickname(data.nickname ?? '')
    } catch {
      showToast({ message: '个人资料加载失败，请稍后重试', variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const data = await getProfileMe(creditPage, PAGE_SIZE)
        if (cancelled) return
        setProfile(data)
        setNickname(data.nickname ?? '')
      } catch {
        if (cancelled) return
        showToast({ message: '个人资料加载失败，请稍后重试', variant: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [creditPage, showToast])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const nextName = nickname.trim()
    if (!nextName || saving) return
    setSaving(true)
    try {
      const data = await updateProfileMe({ nickname: nextName }, creditPage, PAGE_SIZE)
      setProfile(data)
      setNickname(data.nickname ?? nextName)
      showToast({ message: '资料已保存', variant: 'success', autoCloseMs: 1800 })
    } catch {
      showToast({ message: '保存失败，请稍后重试', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const plan = profile?.plan ?? {
    planName: '未开通',
    expiresAt: '—',
    balanceCredits: 0,
    monthlyQuota: 1000,
  }

  const totalLogs = profile?.totalLogs ?? 0
  const totalPages = Math.max(1, Math.ceil(totalLogs / PAGE_SIZE))
  const currentPage = profile?.page ?? Math.min(Math.max(1, creditPage), totalPages)
  const pageRows = useMemo(() => profile?.logs ?? [], [profile])

  useEffect(() => {
    setCreditPage((p) => Math.min(p, totalPages))
  }, [totalPages])

  const shellRound = angular ? 'rounded-none' : 'rounded-[var(--radius)]'
  const innerRound = angular ? 'rounded-none' : 'rounded-2xl'

  return (
    <ContentSkeleton
      title="我的"
      breadcrumbs={[
        { label: '首页', to: '/' },
        { label: '我的' },
      ]}
      adminLayout
      framed={false}
    >
      <ThemeStagger className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <ThemeStaggerItem>
          <section
            className={[
              'overflow-hidden border border-line bg-panel shadow-card',
              shellRound,
            ].join(' ')}
          >
          <div
            className={[
              'relative px-5 py-8 sm:px-8 sm:py-10 lg:px-6 lg:py-7',
              'bg-gradient-to-br from-[var(--surface-muted)] via-panel to-[var(--shell-content-bg)]',
            ].join(' ')}
          >
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 flex-col gap-6 sm:flex-row sm:items-start">
                <div
                  className={[
                    'flex h-20 w-20 shrink-0 items-center justify-center border-2 border-line bg-panel text-2xl font-bold tracking-tight text-ink shadow-sm sm:h-24 sm:w-24',
                    innerRound,
                  ].join(' ')}
                  aria-hidden
                >
                  {((profile?.nickname ?? nickname).trim().slice(0, 1) || '我').toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-soft">
                    账户
                  </p>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                    个人信息
                  </h2>
                  <p className="mt-1 max-w-md text-sm leading-relaxed text-ink-soft">
                    管理展示名称与账户信息。
                  </p>
                  <form
                    id="profile-basic-form"
                    onSubmit={onSubmit}
                    className="mt-5 max-w-md space-y-4"
                  >
                    <div>
                      <label htmlFor="nickname" className="text-xs font-semibold text-ink">
                        昵称
                      </label>
                      <input
                        id="nickname"
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        placeholder="怎么称呼你"
                        className={chromeInputClass(angular)}
                        autoComplete="nickname"
                        disabled={loading || saving}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div
                        className={[
                          'border border-dashed border-line bg-[var(--menu-solid)] px-3 py-2.5',
                          angular ? 'rounded-none' : 'rounded-xl',
                        ].join(' ')}
                      >
                        <div className="text-[11px] font-medium text-ink-soft">用户 Public ID</div>
                        <div className="mt-0.5 truncate font-mono text-xs text-ink">
                          {profile?.publicId ?? '加载中'}
                        </div>
                      </div>
                      <div
                        className={[
                          'border border-dashed border-line bg-[var(--menu-solid)] px-3 py-2.5',
                          angular ? 'rounded-none' : 'rounded-xl',
                        ].join(' ')}
                      >
                        <div className="text-[11px] font-medium text-ink-soft">手机号 / 状态</div>
                        <div className="mt-0.5 text-xs text-ink">
                          {profile?.phone ?? '未绑定'} · {profile?.status ?? '—'}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:flex-col lg:items-end">
                <span className="hidden text-sm text-ink-soft lg:block" aria-hidden>
                  {loading ? '加载中' : `创建于 ${profile?.createdAt ?? '—'}`}
                </span>
                <ChromePrimaryButton
                  type="submit"
                  form="profile-basic-form"
                  disabled={loading || saving}
                >
                  {saving ? '保存中…' : '保存资料'}
                </ChromePrimaryButton>
              </div>
            </div>
          </div>

          <div
            className={[
              'border-t border-line px-5 py-6 sm:px-8 sm:py-7',
              'bg-[var(--surface-muted)]/35',
            ].join(' ')}
          >
            <ProfilePlanStrip
              angular={angular}
              plan={plan}
              onPlanChanged={() => void loadProfile()}
            />
          </div>
          </section>
        </ThemeStaggerItem>

        <ThemeStaggerItem>
          <section
            className={[
              'border border-line bg-panel p-5 shadow-card sm:p-6 lg:p-4',
              shellRound,
            ].join(' ')}
          >
          <h2 className="text-sm font-semibold text-ink">积分记录</h2>
          <ul className="mt-4 divide-y divide-line">
            {loading && pageRows.length === 0 ? (
              <li className="py-6 text-sm text-ink-soft">正在加载积分记录…</li>
            ) : pageRows.length === 0 ? (
              <li className="py-6 text-sm text-ink-soft">暂无积分记录</li>
            ) : pageRows.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-start justify-between gap-2 py-3 first:pt-0"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink">{row.title}</div>
                  <div className="mt-0.5 text-xs text-ink-soft">{row.at}</div>
                </div>
                <div className="shrink-0 text-right text-sm">
                  <span
                    className={
                      row.delta < 0 ? 'font-semibold text-ink' : 'font-semibold text-accent'
                    }
                  >
                    {row.delta > 0 ? '+' : ''}
                    {row.delta}
                  </span>
                  <div className="text-[11px] text-ink-soft">
                    余 {row.balanceAfter.toLocaleString('zh-CN')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {totalPages > 1 ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
              <p className="text-xs text-ink-soft">
                共 <span className="font-medium text-ink">{totalLogs}</span> 条 · 每页{' '}
                {PAGE_SIZE} 条
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={loading || currentPage <= 1}
                  onClick={() => setCreditPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40 hover:bg-surface-muted"
                >
                  上一页
                </button>
                <span className="min-w-[4.5rem] text-center text-xs tabular-nums text-ink-soft">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={loading || currentPage >= totalPages}
                  onClick={() => setCreditPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40 hover:bg-surface-muted"
                >
                  下一页
                </button>
              </div>
            </div>
          ) : null}
          </section>
        </ThemeStaggerItem>

        <UserPermGate perm={USER_PERMISSION.storageGalleryOwn}>
          <ThemeStaggerItem>
            <ProfileStorageSettingsSection angular={angular} canSave={canSaveCloudStorage} />
          </ThemeStaggerItem>
        </UserPermGate>

        <ThemeReveal className="lg:hidden" delay={0.1}>
          <LogoutButton variant="sidebar" />
        </ThemeReveal>
      </ThemeStagger>
    </ContentSkeleton>
  )
}
